import { roundQuantity } from '../schedule/unit-of-measure';

/**
 * A produção pela medida (RN-379).
 *
 * Alvenaria, chapisco, emboço, reboco, gesso e drywall são medidos em m², e quem está no
 * canteiro não sabe a área: sabe que levantou um pano de 2,80 por 5,00 e outro de 2,80 por
 * 3,20. A conta é do sistema — altura × largura de cada pano, somados — e o resultado é a
 * quantidade executada do dia, na precisão da unidade (RN-020).
 *
 * Só o **total** é arredondado. Arredondar cada pano e depois somar acumula o erro de cada
 * um; a soma exata arredondada uma vez é o que a trena mediu.
 */
export interface AreaPanel {
  /** Altura em metros — ou o comprimento, quando o pano é piso ou forro. */
  readonly height: number;
  /** Largura em metros. */
  readonly width: number;
}

/** O pano tem as duas medidas, e as duas são positivas. Sem isso ele não entra na soma. */
export function isValidPanel(panel: AreaPanel): boolean {
  return (
    Number.isFinite(panel.height) &&
    Number.isFinite(panel.width) &&
    panel.height > 0 &&
    panel.width > 0
  );
}

/** A área de um pano, sem arredondar: o arredondamento é do total. */
export function panelArea(panel: AreaPanel): number {
  return panel.height * panel.width;
}

/** A soma dos panos válidos, na precisão da unidade. Sem pano válido, zero. */
export function totalArea(panels: readonly AreaPanel[], decimals: number): number {
  const soma = panels.filter(isValidPanel).reduce((acc, panel) => acc + panelArea(panel), 0);
  return roundQuantity(soma, decimals);
}

/**
 * `"2,80"` → `2.8`. A obra escreve com vírgula, e o teclado numérico do celular também.
 *
 * Vazio, texto ou zero devolvem ausente: a medida que não é número não é medida, e o pano
 * com zero não existe.
 */
export function parseDimension(text: string): number | undefined {
  const limpo = text.trim().replace(',', '.');
  if (limpo === '') return undefined;
  const numero = Number(limpo);
  return Number.isFinite(numero) && numero > 0 ? numero : undefined;
}
